import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAtomValue } from 'jotai';

// Custom hooks
import { useSavedVideos } from './hooks/useSavedVideos';
import { usePersonalPlaylist } from './hooks/usePersonalPlaylist';

// Components
import DeleteModalPlayList from './components/DeleteModalPlayList';
import DeleteModal from './components/DeleteModal';
import MobileViewSearch from '../../../component/MobileViewSearch';
import { AllPlayList } from '../../../component/common/AllPlayList';
import { VideoGrid } from './components/VideoGrid';
import { PlaylistEditModal } from './components/PlaylistEditModal';

// Services and utils
import checkApiStatus from '../../../component/SubscripStatus';
import { userInfoAtom } from '../../../data/atoms/utilsAtoms';
import { PlaylistHeader } from './components/PlaylistHeader';
import playlistApi from '../../../api/playlistApi';
import Toast from '../../../utils/toastManager';
import UndoModal from '../../components/UndoModal';
import Spinner from '../../../component/Spinner';
import { sortVideosByPosition } from '../../../utils/sortVideos';

const hasActiveSubscription = userData =>
	Boolean(userData?.subscriptionId || userData?.user?.subscriptionId || userData?.user?.subscription);

export const PersonalPlayList = () => {
	const userData = useAtomValue(userInfoAtom);
	const location = useLocation();
	const navigate = useNavigate();

	const {
		playlists,
		currentPlaylistIndex,
		setCurrentPlaylistIndex,
		showEditModal,
		setShowEditModal,
		showCreateModal,
		setShowCreateModal,
		showDeleteModal,
		setShowDeleteModal,
		editPlaylistID,
		setPlaylistToDelete,
		showDeleteVideoModal,
		setShowDeleteVideoModal,
		setVideoToDelete,
		handleEditPlaylist,
		handleDeletePlaylist,
		handleDeleteVideo,
		confirmDeletePlaylist,
		confirmDeleteVideo,
		refreshPlaylists
	} = usePersonalPlaylist(userData);

	const { savedVideos, handleSaveUnsave } = useSavedVideos(userData);
	const [subscribtionStatus, setSubscribtionStatus] = useState(() => hasActiveSubscription(userData));
	const [isReordering, setIsReordering] = useState(false);
	const [shuffledData, setShuffledData] = useState([]);
	const [prevVideos, setPrevVideos] = useState([]);
	const [showUndo, setShowUndo] = useState(false);
	const [isLoading, setLoading] = useState(false);
	const [reorderedPayload, setReorderedPayload] = useState(null);

	const saveTimeoutRef = useRef(null);

	useEffect(() => {
		if (location?.pathname === '/personal-playlist' && !userData.user?.userId) {
			navigate('/');
		}
	}, [location, navigate, userData.user?.userId]);

	useEffect(() => {
		const getSubscription = async () => {
			const subscribtionStatus = await checkApiStatus(userData.token);
			setSubscribtionStatus(subscribtionStatus || hasActiveSubscription(userData));
		};
		if (!userData.token) return;
		getSubscription();
	}, [userData]);

	const currentPlaylist = playlists[currentPlaylistIndex];
	const [currentVideos, setCurrentVideos] = useState(sortVideosByPosition(currentPlaylist?.videos) || []);
	useEffect(() => {
		setCurrentVideos(sortVideosByPosition(currentPlaylist?.videos));
	}, [currentPlaylist, isReordering, showUndo]);

	useEffect(() => {
		setShuffledData([]);
	}, [currentPlaylist?._id]);

	const handleShuffle = (e, currentVideos) => {
		e.preventDefault();
		const shuffledArray = [...currentVideos];
		for (let i = shuffledArray.length - 1; i > 0; i--) {
			const j = Math.floor(Math.random() * (i + 1));
			[shuffledArray[i], shuffledArray[j]] = [shuffledArray[j], shuffledArray[i]];
		}

		setPrevVideos(currentVideos);
		setCurrentVideos(shuffledArray);
		setShuffledData(shuffledArray);

		setShowUndo(true);
		const reorderedPositions = shuffledArray.map((item, index) => ({
			_id: item._id,
			newPosition: index
		}));

		const newPayload = {
			type: currentPlaylist?.type,
			playlistId: currentPlaylist?._id,
			reorderedPositions
		};

		setReorderedPayload(newPayload);

		Toast.showToast(<UndoModal onSave={() => handleSaveOrder(newPayload)} onUndo={handleUndo} />, { duration: 5000 });
	};

	const handleReorderClicked = () => {
		setIsReordering(true);
		setPrevVideos(currentVideos);
	};
	const handleReorder = (next, updates) => {
		const reorderedPositions = next.map((item, index) => ({
			_id: item._id,
			newPosition: index
		}));

		setCurrentVideos(next);
		setReorderedPayload({
			type: currentPlaylist.type,
			playlistId: currentPlaylist._id,
			reorderedPositions
		});
	};

	const handleSaveOrder = async (payload = reorderedPayload) => {
		if (payload === undefined || payload === null) {
			setIsReordering(false);
			return;
		}

		try {
			setLoading(true);
			const response = await playlistApi.saveNewOrderToBackend(payload);

			refreshPlaylists();
			setIsReordering(false);

			Toast.success('Playlist reordered successfully!');
		} catch (err) {
			Toast.error('Error saving order');
		} finally {
			setLoading(false);
		}
	};

	const handleUndo = async () => {
		setLoading(true);
		clearTimeout(saveTimeoutRef.current);

		setCurrentVideos([...prevVideos]);
		refreshPlaylists();
		setLoading(false);
		window.location.reload();
		setShowUndo(false);
	};

	return (
		<>
			<div>{isLoading ? <Spinner /> : ''}</div>
			<div className="row m-0 c-mobile_honm-serch update">
				<MobileViewSearch />
			</div>

			<div>
				<div className="tab-content">
					<div id="menu2" className="tab-pane fade active show">
						{/* Playlists selector component */}
						<AllPlayList
							title="PERSONAL PLAYLISTS"
							data={playlists}
							nshowModal={showCreateModal}
							setnModalShow={setShowCreateModal}
							page="Personal"
							currentIndex={currentPlaylistIndex}
							setCurrentIndex={setCurrentPlaylistIndex}
							playlistType={'personal-playlist'}
							handleModalSubmit={() => {
								setShowEditModal(false);
								refreshPlaylists();
							}}
							subscribtionStatus={subscribtionStatus}
							isEditMode={isReordering}
							onDelete={handleDeletePlaylist}
						/>

						{/* Edit playlist modal */}
						<PlaylistEditModal
							showModal={showEditModal}
							setModalShow={setShowEditModal}
							handleClose={() => {
								setShowEditModal(false);
								refreshPlaylists();
							}}
							editPlaylistID={editPlaylistID}
							setnModalShow={setShowCreateModal}
							nshowModal={showCreateModal}
						/>

						{/* Main content area */}
						<div
							className="c-video c-video_feature-box update updated__play__card__list"
							style={{ backgroundColor: 'var(--dark)' }}
						>
							{currentPlaylist ? (
								<div className="row m-0 mt-2 list_id_pt_top position-relative" id={currentPlaylistIndex}>
									<div className="position-absolute start-0 w-100" style={{ top: '-100px' }}></div>
									<div className="col-md-12 p-0">
										<div className="c-featured_box-right">
											<PlaylistHeader
												playlist={currentPlaylist}
												onShuffle={handleShuffle}
												onEdit={e => {
													setIsReordering(false);
													handleEditPlaylist(e, currentPlaylist);
												}}
												onDelete={handleDeletePlaylist}
												onReorderClicked={handleReorderClicked}
												isReordering={isReordering}
												onSave={() => handleSaveOrder()}
											/>

											{/* Videos grid */}
											<VideoGrid
												videos={shuffledData.length > 0 ? shuffledData : currentVideos}
												playlistId={currentPlaylist._id}
												playlist={currentPlaylist}
												savedVideos={savedVideos}
												onDelete={handleDeleteVideo}
												onReorder={handleReorder}
												onSaveUnsave={handleSaveUnsave}
												isReordering={isReordering}
												subscriptionStatus={subscribtionStatus}
											/>
										</div>
									</div>
								</div>
							) : (
								<div className="text-center p-4">Create your own Personal Playlists now!</div>
							)}
						</div>
					</div>
				</div>
			</div>

			{/* Modals */}
			<DeleteModal
				modal={showDeleteVideoModal}
				onConfirmAction={confirmDeleteVideo}
				onCancelAction={() => {
					setVideoToDelete(null);
					setShowDeleteVideoModal(false);
				}}
			/>

			<DeleteModalPlayList
				modal={showDeleteModal}
				onConfirmAction={confirmDeletePlaylist}
				onCancelAction={() => {
					setPlaylistToDelete(null);
					setShowDeleteModal(false);
				}}
			/>
		</>
	);
};
